import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown,
  Settings,
  CreditCard,
  Sparkles,
  Shield,
  LogOut,
  User as UserIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UserMenu — top-right authenticated user dropdown.
 *
 * Renders the user's avatar, name, and plan badge. Clicking opens a dropdown
 * with:
 *   • Account Settings → /account
 *   • Manage Subscription (paid users)  → opens Stripe Customer Portal
 *   • Upgrade Plan       (free users)   → /pricing
 *   • Security           → /account?section=security
 *   • Sign Out
 *
 * NOTE: Password forms are intentionally NOT placed inside the dropdown.
 * The dropdown is navigation; /account is the management surface.
 */
export default function UserMenu({ user, className = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const plan = user?.subscription_plan || 'free';
  const isPaid = plan === 'pro' || plan === 'premium';

  const planLabel =
    plan === 'premium'
      ? t('common.premium')
      : plan === 'pro'
      ? t('common.pro')
      : t('common.free');

  const badgeClasses =
    plan === 'premium'
      ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B]'
      : plan === 'pro'
      ? 'bg-gradient-to-r from-[#45C04A] to-[#65D95A] text-[#12355B]'
      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200';

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success(t('nav.signedOut'));
    } catch {
      /* even on failure, drop the client to a signed-out state */
    } finally {
      // P1A: wipe all sessionStorage-backed Scenario A/B/C slots so signing
      // in as a different user cannot leak the previous user's scenarios.
      try {
        // Late-imported to avoid a hard dependency on the scenario module
        // in bundles that never hit the Scenario Analyzer.
        const mod = await import('./scenario/scenarioSessionStorage');
        mod.clearAllScenarioSessions?.();
      } catch { /* silent */ }
      navigate('/');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/subscription/portal`,
        {},
        { withCredentials: true },
      );
      const url = resp?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error(t('account.billing.portalError'));
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        // No stripe_customer_id yet — nudge to pricing.
        toast.error(t('account.billing.noSubscription'));
        navigate('/pricing');
      } else {
        toast.error(t('account.billing.portalError'));
      }
    }
  };

  const initials = ((user?.name || user?.email || '?').match(/\b\w/g) || ['?'])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          aria-label={t('nav.openAccountMenu')}
          className={`group inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-2 sm:pr-3 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#45C04A]/40 ${className}`}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || ''}
              className="w-8 h-8 rounded-full ring-2 ring-slate-200 dark:ring-slate-700"
            />
          ) : (
            <span
              aria-hidden
              className="w-8 h-8 rounded-full bg-[#12355B] text-white flex items-center justify-center text-xs font-bold ring-2 ring-slate-200 dark:ring-slate-700"
            >
              {initials}
            </span>
          )}
          <span
            data-testid="user-menu-name"
            className="hidden sm:block text-sm font-medium text-[#12355B] dark:text-white max-w-[10rem] truncate"
          >
            {user?.name || user?.email}
          </span>
          <span
            data-testid="user-menu-plan-badge"
            className={`hidden md:inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${badgeClasses}`}
          >
            {planLabel}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="user-menu-content"
        align="end"
        sideOffset={8}
        className="min-w-[16rem] rounded-xl bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 shadow-xl p-1.5"
      >
        <DropdownMenuLabel className="px-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-[#12355B] dark:text-white truncate">
              {user?.name || t('nav.account')}
            </span>
            {user?.email && (
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {user.email}
              </span>
            )}
            <span
              className={`mt-1.5 inline-flex w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${badgeClasses}`}
            >
              {planLabel}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuItem
          data-testid="user-menu-account-item"
          onSelect={() => navigate('/account')}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#12355B] dark:text-slate-100 focus:bg-[#45C04A]/10 dark:focus:bg-[#45C04A]/20 focus:text-[#12355B] dark:focus:text-white"
        >
          <Settings className="w-4 h-4 mr-2 text-slate-500 dark:text-slate-400" />
          {t('nav.accountSettings')}
        </DropdownMenuItem>

        {isPaid ? (
          <DropdownMenuItem
            data-testid="user-menu-manage-subscription-item"
            onSelect={handleManageSubscription}
            className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#12355B] dark:text-slate-100 focus:bg-[#45C04A]/10 dark:focus:bg-[#45C04A]/20 focus:text-[#12355B] dark:focus:text-white"
          >
            <CreditCard className="w-4 h-4 mr-2 text-slate-500 dark:text-slate-400" />
            {t('nav.manageSubscription')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            data-testid="user-menu-upgrade-item"
            onSelect={() => navigate('/pricing')}
            className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#12355B] dark:text-slate-100 focus:bg-[#45C04A]/10 dark:focus:bg-[#45C04A]/20 focus:text-[#12355B] dark:focus:text-white"
          >
            <Sparkles className="w-4 h-4 mr-2 text-[#45C04A]" />
            {t('nav.upgradePlan')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          data-testid="user-menu-security-item"
          onSelect={() => navigate('/account?section=security')}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-[#12355B] dark:text-slate-100 focus:bg-[#45C04A]/10 dark:focus:bg-[#45C04A]/20 focus:text-[#12355B] dark:focus:text-white"
        >
          <Shield className="w-4 h-4 mr-2 text-slate-500 dark:text-slate-400" />
          {t('nav.security')}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuItem
          data-testid="user-menu-logout-item"
          onSelect={handleLogout}
          className="cursor-pointer rounded-lg px-3 py-2 text-sm text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/40 focus:text-red-700 dark:focus:text-red-300"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('nav.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Named export for consistency with other components.
export { UserMenu };
